import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { getProficiencyGrid, bulkSaveProficiency } from "../lib/api";
import { semesterOptions } from "../lib/semesters";

const TRANSFERABLE_SCORES = [
  { value: "", label: "—", color: "" },
  { value: "1", label: "1", color: "bg-red-100" },
  { value: "2", label: "2", color: "bg-amber-100" },
  { value: "3", label: "3", color: "bg-blue-100" },
  { value: "4", label: "4", color: "bg-green-200" },
  { value: "N/A", label: "N/A", color: "bg-gray-100" },
];

const TRANSFERABLE_LEGEND = "1 = Infrequently   2 = Sometimes   3 = Regularly   4 = Consistently";

const ACADEMIC_SCORES = [
  { value: "", label: "—", color: "" },
  { value: "N/A", label: "N/A", color: "bg-gray-100" },
  { value: "1", label: "1", color: "bg-red-100" },
  { value: "2", label: "2", color: "bg-amber-100" },
  { value: "3", label: "3", color: "bg-green-200" },
];

const ACADEMIC_LEGEND = "1 = Below proficiency   2 = Approaching   3 = Demonstrated proficiency";

function currentSemester() {
  const d = new Date();
  const m = d.getMonth();
  const y = d.getFullYear();
  if (m >= 7) return { semester: `Fall-${y}`, school_year: `${y}-${y + 1}` };
  return { semester: `Spring-${y}`, school_year: `${y - 1}-${y}` };
}

export default function ProficiencyGridPage() {
  const [type, setType] = useState("transferable");
  const [{ semester, school_year }, setPeriod] = useState(currentSemester());
  const [data, setData] = useState({ indicators: [], students: [], cells: {} });
  const [pending, setPending] = useState({}); // { "studentId|indicatorId": score }
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // Semester dropdown options (Fall / Spring / Summer) for the current school year.
  const semOptions = useMemo(() => semesterOptions(school_year), [school_year]);
  // Snap the semester to a valid option when the school year changes.
  useEffect(() => {
    const values = semOptions.map((o) => o.value);
    if (values.length && !values.includes(semester)) {
      setPeriod((p) => ({ ...p, semester: values[0] }));
    }
  }, [semOptions, semester]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getProficiencyGrid({ indicator_type: type, semester, school_year });
      setData(res.data);
      setPending({});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load grid");
    } finally {
      setLoading(false);
    }
  }, [type, semester, school_year]);

  useEffect(() => { load(); }, [load]);

  const handleCellChange = (studentId, indicatorId, score) => {
    setPending((p) => ({ ...p, [`${studentId}|${indicatorId}`]: score }));
  };

  const pendingCount = Object.keys(pending).length;

  const handleSave = async () => {
    if (pendingCount === 0) return;
    setSaving(true);
    try {
      const entries = Object.entries(pending).map(([key, score]) => {
        const [student_id, indicator_id] = key.split("|");
        return {
          student_id,
          indicator_id,
          score: score || null,
          times_demonstrated: score === "3" ? 1 : 0,
        };
      });
      const res = await bulkSaveProficiency({ semester, school_year, entries });
      toast.success(`Saved ${res.data.saved} entries`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const scoreOptions = type === "transferable" ? TRANSFERABLE_SCORES : ACADEMIC_SCORES;
  const legend = type === "transferable" ? TRANSFERABLE_LEGEND : ACADEMIC_LEGEND;

  // Group indicators by category for visual breaks
  const grouped = useMemo(() => {
    const out = [];
    let lastCat = null;
    for (const ind of data.indicators) {
      const cat = `${ind.subject}${ind.category ? " — " + ind.category : ""}`;
      if (cat !== lastCat) {
        out.push({ kind: "header", label: cat, key: `h-${out.length}-${cat}` });
        lastCat = cat;
      }
      out.push({ kind: "indicator", ind });
    }
    return out;
  }, [data.indicators]);

  const cellValueFor = (studentId, indicatorId) => {
    const k = `${studentId}|${indicatorId}`;
    if (pending[k] !== undefined) return pending[k];
    return data.cells?.[studentId]?.[indicatorId]?.score || "";
  };

  return (
    <div className="space-y-4 p-4 max-w-full" data-testid="proficiency-grid-page">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle>Proficiency Bulk Grid</CardTitle>
              <CardDescription>{legend}</CardDescription>
            </div>
            <div className="flex items-end gap-3 flex-wrap">
              <div className="space-y-1">
                <Label className="text-xs">Semester</Label>
                {semOptions.length ? (
                  <Select value={semester} onValueChange={(v) => setPeriod((p) => ({ ...p, semester: v }))}>
                    <SelectTrigger className="w-40" data-testid="grid-semester-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {semOptions.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input className="w-32" value={semester} onChange={(e) => setPeriod((p) => ({ ...p, semester: e.target.value }))} data-testid="grid-semester-input" />
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">School Year</Label>
                <Input className="w-32" value={school_year} onChange={(e) => setPeriod((p) => ({ ...p, school_year: e.target.value }))} data-testid="grid-year-input" />
              </div>
              <Button onClick={handleSave} disabled={saving || pendingCount === 0} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="grid-save-btn">
                {saving ? "Saving…" : pendingCount > 0 ? `Save ${pendingCount} change${pendingCount === 1 ? "" : "s"}` : "Save"}
              </Button>
            </div>
          </div>
          <Tabs value={type} onValueChange={setType} className="pt-3">
            <TabsList>
              <TabsTrigger value="transferable" data-testid="grid-type-transferable">Transferable Skills</TabsTrigger>
              <TabsTrigger value="academic" data-testid="grid-type-academic">Academic & Electives</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-auto">
          {loading ? (
            <p className="text-center py-12 text-muted-foreground">Loading…</p>
          ) : data.students.length === 0 ? (
            <p className="text-center py-12 text-muted-foreground">No students found.</p>
          ) : (
            <table className="text-sm border-collapse min-w-full" data-testid="proficiency-grid-table">
              <thead className="sticky top-0 z-10 bg-[#00305E] text-white">
                <tr>
                  <th className="text-left px-3 py-2 sticky left-0 z-20 bg-[#00305E] min-w-[280px]">Indicator</th>
                  {data.students.map((s) => (
                    <th key={s.id} className="text-center px-2 py-2 font-medium whitespace-nowrap" title={`${s.first_name} ${s.last_name}`}>
                      {s.preferred_name || s.first_name}
                      <div className="text-[10px] font-normal opacity-75">{s.last_name}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grouped.map((row) => {
                  if (row.kind === "header") {
                    return (
                      <tr key={row.key}>
                        <td colSpan={data.students.length + 1} className="bg-[#00305E]/10 px-3 py-2 font-semibold text-[#00305E] text-xs uppercase tracking-wide">
                          {row.label}
                        </td>
                      </tr>
                    );
                  }
                  const ind = row.ind;
                  return (
                    <tr key={ind.id} className="border-b hover:bg-muted/10">
                      <td className="px-3 py-1.5 sticky left-0 bg-white border-r text-xs leading-snug min-w-[280px] max-w-[400px]">
                        {ind.indicator_label && <span className="font-semibold mr-1">{ind.indicator_label}:</span>}
                        {ind.indicator_text}
                      </td>
                      {data.students.map((s) => {
                        const value = cellValueFor(s.id, ind.id);
                        const opt = scoreOptions.find((o) => o.value === value);
                        const isPending = pending[`${s.id}|${ind.id}`] !== undefined;
                        return (
                          <td key={s.id} className={`p-0 text-center border ${opt?.color || ""} ${isPending ? "ring-2 ring-amber-400 ring-inset" : ""}`}>
                            <select
                              value={value}
                              onChange={(e) => handleCellChange(s.id, ind.id, e.target.value)}
                              className="w-full h-full px-1 py-1.5 bg-transparent text-center text-xs font-semibold focus:outline-none cursor-pointer"
                              data-testid={`cell-${s.id}-${ind.id}`}
                            >
                              {scoreOptions.map((o) => (
                                <option key={o.value || "blank"} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {pendingCount > 0 && (
        <div className="fixed bottom-6 right-6 bg-amber-50 border-2 border-amber-400 rounded-lg p-4 shadow-lg flex items-center gap-3" data-testid="pending-changes-bar">
          <Badge variant="outline" className="border-amber-500 text-amber-700">{pendingCount} unsaved</Badge>
          <Button onClick={handleSave} disabled={saving} className="bg-[#00305E] hover:bg-[#00305E]/90">
            {saving ? "Saving…" : "Save now"}
          </Button>
        </div>
      )}
    </div>
  );
}
